// eslint-disable-next-line no-restricted-imports
import {
  AccountBalance as AccountBalanceSVG,
  Add as AddSVG,
  ArrowForwardIos as ArrowForwardIosSVG,
  AttachMoney as AttachMoneySVG,
  Business as BusinessSVG,
  Cancel as CancelSVG,
  CardGiftcard as CardGiftcardSVG,
  Check as CheckSVG,
  CheckCircle as CheckCircleSVG,
  Close as CloseSVG,
  CloudDownload as CloudDownloadSVG,
  CloudOff as CloudOffSVG,
  CorporateFare as CorporateFareSVG,
  CreditCard as CreditCardSVG,
  Delete as DeleteSVG,
  DepartureBoard as DepartureBoardSVG,
  DirectionsCar as DirectionCarSVG,
  Diversity3 as Diversity3SVG,
  East as EastSVG,
  Edit as EditSVG,
  Email as EmailSVG,
  ErrorOutline as ErrorOutlineSVG,
  EventNote as EventNoteSVG,
  FileDownload as FileDownloadSVG,
  FileDownloadOff as FileDownloadOffSVG,
  FileUpload as FileUploadSVG,
  Group as GroupSVG,
  HideSource as HideSourceSVG,
  HourglassEmpty as HourglassEmptySVG,
  KeyboardArrowDown as KeyboardArrowDownSVG,
  KeyboardArrowUp as KeyboardArrowUpSVG,
  Launch as LaunchSVG,
  Lock as LockSVG,
  LockOpen as LockOpenSVG,
  Logout as LogoutSVG,
  Menu as MenuSVG,
  MoneyOff as MoneyOffSVG,
  Notifications as NotificationsSVG,
  PlayArrow as PlayArrowSVG,
  QueryStats as QueryStatsSVG,
  QuestionMark as QuestionMarkSVG,
  Receipt as ReceiptSVG,
  ReceiptLong as ReceiptLongSVG,
  Restore as RestoreSVG,
  Search as SearchSVG,
  Send as SendSVG,
  Settings as SettingsSVG,
  ShoppingBasket as ShoppingBasketSVG,
  ShoppingCart as ShoppingCartSVG,
  Stop as StopSVG,
} from '@mui/icons-material';
import { Box, Theme, useTheme } from '@mui/material';
import { Color } from 'types/shared';

import { getColorFromTheme } from 'core/color';

type Size = 'extraSmall' | 'small' | 'medium' | 'large' | 'flex';

interface SVGProps {
  color: Color;
  size: Size;
}

interface GetSizeProps {
  size: Size | undefined;
  theme: Theme;
}

function getSize({ size, theme }: GetSizeProps) {
  switch (size) {
    case 'extraSmall':
      return theme.spacing(2);
    case 'small':
      return theme.spacing(3);
    case 'medium':
      return theme.spacing(4);
    case 'large':
      return theme.spacing(8);
    case 'flex':
      return '100%';
    default:
      return theme.spacing(4);
  }
}

function getProps({ color = 'common.black', size, theme }: SVGProps & { theme: Theme }) {
  return {
    color: getColorFromTheme({ color, theme }),
    width: getSize({ size, theme }),
    height: getSize({ size, theme }),
  };
}

function SVG({ color, size }: SVGProps) {
  const theme = useTheme();
  return <QuestionMarkSVG sx={getProps({ color, size, theme })} />;
}

function AccountBalance({ color, size }: SVGProps) {
  const theme = useTheme();
  return <AccountBalanceSVG sx={getProps({ color, size, theme })} />;
}

function Add({ color, size }: SVGProps) {
  const theme = useTheme();
  return <AddSVG sx={getProps({ color, size, theme })} />;
}

function ArrowForwardIos({ color, size }: SVGProps) {
  const theme = useTheme();
  return <ArrowForwardIosSVG sx={getProps({ color, size, theme })} />;
}

function AttachMoney({ color, size }: SVGProps) {
  const theme = useTheme();
  return <AttachMoneySVG sx={getProps({ color, size, theme })} />;
}

function Business({ color, size }: SVGProps) {
  const theme = useTheme();
  return <BusinessSVG sx={getProps({ color, size, theme })} />;
}

function Cancel({ color, size }: SVGProps) {
  const theme = useTheme();
  return <CancelSVG sx={getProps({ color, size, theme })} />;
}

function CardGiftcard({ color, size }: SVGProps) {
  const theme = useTheme();
  return <CardGiftcardSVG sx={getProps({ color, size, theme })} />;
}

function Check({ color, size }: SVGProps) {
  const theme = useTheme();
  return <CheckSVG sx={getProps({ color, size, theme })} />;
}

function CheckCircle({ color, size }: SVGProps) {
  const theme = useTheme();
  return <CheckCircleSVG sx={getProps({ color, size, theme })} />;
}

function Close({ color, size }: SVGProps) {
  const theme = useTheme();
  return <CloseSVG sx={getProps({ color, size, theme })} />;
}

function CloudDownload({ color, size }: SVGProps) {
  const theme = useTheme();
  return <CloudDownloadSVG sx={getProps({ color, size, theme })} />;
}

function CloudOff({ color, size }: SVGProps) {
  const theme = useTheme();
  return <CloudOffSVG sx={getProps({ color, size, theme })} />;
}

function CorporateFare({ color, size }: SVGProps) {
  const theme = useTheme();
  return <CorporateFareSVG sx={getProps({ color, size, theme })} />;
}

function CreditCard({ color, size }: SVGProps) {
  const theme = useTheme();
  return <CreditCardSVG sx={getProps({ color, size, theme })} />;
}

function Delete({ color, size }: SVGProps) {
  const theme = useTheme();
  return <DeleteSVG sx={getProps({ color, size, theme })} />;
}

function DepartureBoard({ color, size }: SVGProps) {
  const theme = useTheme();
  return <DepartureBoardSVG sx={getProps({ color, size, theme })} />;
}

function DirectionCar({ color, size }: SVGProps) {
  const theme = useTheme();
  return <DirectionCarSVG sx={getProps({ color, size, theme })} />;
}

function Diversity3({ color, size }: SVGProps) {
  const theme = useTheme();
  return <Diversity3SVG sx={getProps({ color, size, theme })} />;
}

function East({ color, size }: SVGProps) {
  const theme = useTheme();
  return <EastSVG sx={getProps({ color, size, theme })} />;
}

function Edit({ color, size }: SVGProps) {
  const theme = useTheme();
  return <EditSVG sx={getProps({ color, size, theme })} />;
}

function ErrorOutline({ color, size }: SVGProps) {
  const theme = useTheme();
  return <ErrorOutlineSVG sx={getProps({ color, size, theme })} />;
}

function EventNote({ color, size }: SVGProps) {
  const theme = useTheme();
  return <EventNoteSVG sx={getProps({ color, size, theme })} />;
}

function Email({ color, size }: SVGProps) {
  const theme = useTheme();
  return <EmailSVG sx={getProps({ color, size, theme })} />;
}

function FileDownload({ color, size }: SVGProps) {
  const theme = useTheme();
  return <FileDownloadSVG sx={getProps({ color, size, theme })} />;
}

function FileDownloadOff({ color, size }: SVGProps) {
  const theme = useTheme();
  return <FileDownloadOffSVG sx={getProps({ color, size, theme })} />;
}

function FileUpload({ color, size }: SVGProps) {
  const theme = useTheme();
  return <FileUploadSVG sx={getProps({ color, size, theme })} />;
}

function Group({ color, size }: SVGProps) {
  const theme = useTheme();
  return <GroupSVG sx={getProps({ color, size, theme })} />;
}

function HideSource({ color, size }: SVGProps) {
  const theme = useTheme();
  return <HideSourceSVG sx={getProps({ color, size, theme })} />;
}

function HourglassEmpty({ color, size }: SVGProps) {
  const theme = useTheme();
  return <HourglassEmptySVG sx={getProps({ color, size, theme })} />;
}

function KeyboardArrowDown({ color, size }: SVGProps) {
  const theme = useTheme();
  return <KeyboardArrowDownSVG sx={getProps({ color, size, theme })} />;
}

function KeyboardArrowUp({ color, size }: SVGProps) {
  const theme = useTheme();
  return <KeyboardArrowUpSVG sx={getProps({ color, size, theme })} />;
}

function Launch({ color, size }: SVGProps) {
  const theme = useTheme();
  return <LaunchSVG sx={getProps({ color, size, theme })} />;
}

function Lock({ color, size }: SVGProps) {
  const theme = useTheme();
  return <LockSVG sx={getProps({ color, size, theme })} />;
}

function LockOpen({ color, size }: SVGProps) {
  const theme = useTheme();
  return <LockOpenSVG sx={getProps({ color, size, theme })} />;
}

function Logout({ color, size }: SVGProps) {
  const theme = useTheme();
  return <LogoutSVG sx={getProps({ color, size, theme })} />;
}

function Logo({ color, size }: SVGProps) {
  const theme = useTheme();
  const props = getProps({ color, size, theme });

  return (
    <Box sx={{ ...props }}>
      <svg viewBox="0 0 137 24" xmlns="http://www.w3.org/2000/svg">
        <g fill={props.color} fillRule="nonzero">
          <path d="M87.15.43L83.96 7.8c-.27.7-.72 1.3-1.31 1.76-.6.4-1.26.66-1.96.75l-.3-1.04c.52-.1 1-.28 1.46-.56.35-.26.63-.62.79-1.03l.24-.57L80 .43h1.45l2.14 5.28L85.78.43h1.37zm1.87 6.48c-.5-.28-.91-.71-1.17-1.23a4 4 0 0 1-.42-1.86c-.01-.65.13-1.3.41-1.88A2.9 2.9 0 0 1 89.02.71c1.1-.58 2.4-.58 3.5 0 .5.28.91.71 1.17 1.23.29.59.43 1.23.41 1.88a4 4 0 0 1-.41 1.86c-.26.52-.67.95-1.17 1.23-1.1.58-2.41.58-3.5 0zm3.19-1.27c.34-.41.5-1.02.5-1.82s-.17-1.41-.5-1.82a1.72 1.72 0 0 0-1.44-.64c-.55-.03-1.1.2-1.45.64a2.82 2.82 0 0 0-.5 1.82c0 .8.16 1.4.5 1.82a1.99 1.99 0 0 0 2.89 0zm9.15-5.2v6.81h-1.33V6.11c-.2.39-.51.7-.89.92-.4.21-.84.32-1.29.31-1.63 0-2.45-.9-2.45-2.72V.43h1.36V4.6c-.03.44.09.88.35 1.24.27.28.66.43 1.05.4.5.02 1-.18 1.34-.55a2 2 0 0 0 .51-1.43V.43h1.35zm5.78-.07l-.03 1.24a2.3 2.3 0 0 0-.8-.13 1.72 1.72 0 0 0-1.4.56c-.32.4-.48.9-.46 1.41v3.8h-1.37v-4.9c0-.63-.03-1.27-.1-1.9h1.27l.12 1.23c.16-.43.45-.8.84-1.04a2.35 2.35 0 0 1 1.93-.27zM82.12 20.43c-.5-.28-.9-.71-1.17-1.23a4 4 0 0 1-.4-1.86c-.01-.65.13-1.3.41-1.88.26-.52.67-.95 1.17-1.23 1.1-.58 2.41-.58 3.51 0 .5.28.91.71 1.17 1.23.29.59.43 1.23.41 1.88.02.64-.12 1.28-.4 1.86-.27.52-.68.95-1.18 1.23-1.1.58-2.4.58-3.5 0h-.02zm3.2-1.27c.33-.41.5-1.02.5-1.82a2.85 2.85 0 0 0-.51-1.82 1.73 1.73 0 0 0-1.43-.63c-.55-.03-1.1.2-1.45.63a2.81 2.81 0 0 0-.51 1.82c0 .8.17 1.4.5 1.82a2 2 0 0 0 2.9 0zm12.37-5.2h1.34l-2.6 6.81H95.2l-1.83-4.93-1.82 4.93H90.3l-2.6-6.81h1.42l1.86 5.2 1.88-5.2h1.09l1.88 5.2 1.86-5.2zm8.42 2.56v4.25h-1.36v-4.16c0-.6-.12-1.03-.35-1.3-.28-.3-.68-.44-1.09-.4a1.83 1.83 0 0 0-1.39.54c-.36.39-.55.91-.52 1.44v3.88h-1.36v-4.9c0-.63-.04-1.27-.11-1.9h1.27l.12 1.17c.2-.42.53-.76.94-1 .43-.22.92-.34 1.4-.33 1.64-.01 2.45.9 2.45 2.71zm14.27-2.56h1.34l-2.6 6.81h-1.25l-1.82-4.94-1.82 4.94H113l-2.6-6.82h1.42l1.85 5.2 1.89-5.2h1.09l1.87 5.21 1.87-5.2zm8.5 0v6.81h-1.36v-1.16c-.2.4-.5.72-.9.93-.41.22-.88.34-1.36.33a2.82 2.82 0 0 1-2.66-1.66c-.26-.58-.39-1.21-.38-1.85-.01-.64.12-1.28.38-1.86.24-.52.62-.95 1.1-1.26.46-.3 1-.45 1.56-.45.48 0 .95.1 1.37.33.38.22.69.55.89.95v-1.11h1.36zm-1.86 5.18c.36-.53.54-1.16.5-1.8a2.85 2.85 0 0 0-.51-1.82 1.93 1.93 0 0 0-2.89.03 2.85 2.85 0 0 0-.52 1.81c-.04.64.15 1.27.52 1.79.36.43.9.66 1.46.63.55.03 1.08-.2 1.43-.64zm9.78-5.18l-3.18 7.36c-.27.7-.73 1.3-1.32 1.76-.59.4-1.26.66-1.96.75l-.3-1.05c.52-.1 1.01-.28 1.46-.56.36-.26.63-.62.8-1.03l.23-.57-2.86-6.66h1.45l2.13 5.27 2.18-5.27h1.37zM11.76.27a1.56 1.56 0 1 1 0 3.12H3.38v5.73h7.65a1.55 1.55 0 1 1 0 3.1H3.38v7.06a1.55 1.55 0 1 1-3.1 0V1.82C.27.97.96.27 1.82.27h9.93zm7.61 0c.86 0 1.56.7 1.56 1.55v17.46a1.55 1.55 0 1 1-3.11 0V1.82c0-.85.7-1.55 1.55-1.55zm41.32 0a10.3 10.3 0 1 1-6.63 18.15 1.55 1.55 0 1 1 2-2.37A7.2 7.2 0 0 0 67.2 7.53a7.19 7.19 0 0 0-13.7 3.02c0 .86-.69 1.56-1.55 1.56h-5.71a10.3 10.3 0 1 1-3.55-9.43 1.55 1.55 0 1 1-2 2.38 7.18 7.18 0 0 0-11.44 7.76 7.18 7.18 0 0 0 13.8-.72h-7a1.55 1.55 0 1 1 0-3.1H50.5A10.3 10.3 0 0 1 60.7.27z" />
        </g>
      </svg>
    </Box>
  );
}

function Menu({ color, size }: SVGProps) {
  const theme = useTheme();
  return <MenuSVG sx={getProps({ color, size, theme })} />;
}

function MoneyOff({ color, size }: SVGProps) {
  const theme = useTheme();
  return <MoneyOffSVG sx={getProps({ color, size, theme })} />;
}

function Notifications({ color, size }: SVGProps) {
  const theme = useTheme();
  return <NotificationsSVG sx={getProps({ color, size, theme })} />;
}

function PlayArrow({ color, size }: SVGProps) {
  const theme = useTheme();
  return <PlayArrowSVG sx={getProps({ color, size, theme })} />;
}

function QueryStats({ color, size }: SVGProps) {
  const theme = useTheme();
  return <QueryStatsSVG sx={getProps({ color, size, theme })} />;
}

function QuestionMark({ color, size }: SVGProps) {
  const theme = useTheme();
  return <QuestionMarkSVG sx={getProps({ color, size, theme })} />;
}

function Receipt({ color, size }: SVGProps) {
  const theme = useTheme();
  return <ReceiptSVG sx={getProps({ color, size, theme })} />;
}

function ReceiptLong({ color, size }: SVGProps) {
  const theme = useTheme();
  return <ReceiptLongSVG sx={getProps({ color, size, theme })} />;
}

function Restore({ color, size }: SVGProps) {
  const theme = useTheme();
  return <RestoreSVG sx={getProps({ color, size, theme })} />;
}

function Search({ color, size }: SVGProps) {
  const theme = useTheme();
  return <SearchSVG sx={getProps({ color, size, theme })} />;
}

function Send({ color, size }: SVGProps) {
  const theme = useTheme();
  return <SendSVG sx={getProps({ color, size, theme })} />;
}

function Settings({ color, size }: SVGProps) {
  const theme = useTheme();
  return <SettingsSVG sx={getProps({ color, size, theme })} />;
}

function ShoppingBasket({ color, size }: SVGProps) {
  const theme = useTheme();
  return <ShoppingBasketSVG sx={getProps({ color, size, theme })} />;
}

function ShoppingCart({ color, size }: SVGProps) {
  const theme = useTheme();
  return <ShoppingCartSVG sx={getProps({ color, size, theme })} />;
}

function Stop({ color, size }: SVGProps) {
  const theme = useTheme();
  return <StopSVG sx={getProps({ color, size, theme })} />;
}

const svgMapping = {
  accountBalance: AccountBalance,
  add: Add,
  arrowForwardIos: ArrowForwardIos,
  attachMoney: AttachMoney,
  business: Business,
  cancel: Cancel,
  cardGiftcard: CardGiftcard,
  check: Check,
  checkCircle: CheckCircle,
  close: Close,
  cloudDownload: CloudDownload,
  cloudOff: CloudOff,
  corporateFare: CorporateFare,
  creditCard: CreditCard,
  delete: Delete,
  departureBoard: DepartureBoard,
  directionCar: DirectionCar,
  diversity3: Diversity3,
  east: East,
  edit: Edit,
  email: Email,
  errorOutline: ErrorOutline,
  eventNote: EventNote,
  fileDownload: FileDownload,
  fileDownloadOff: FileDownloadOff,
  fileUpload: FileUpload,
  group: Group,
  hideSource: HideSource,
  hourglassEmpty: HourglassEmpty,
  keyboardArrowDown: KeyboardArrowDown,
  keyboardArrowUp: KeyboardArrowUp,
  launch: Launch,
  lock: Lock,
  lockOpen: LockOpen,
  logout: Logout,
  logo: Logo,
  menu: Menu,
  moneyOff: MoneyOff,
  notifications: Notifications,
  playArrow: PlayArrow,
  queryStats: QueryStats,
  questionMark: QuestionMark,
  receipt: Receipt,
  receiptLong: ReceiptLong,
  restore: Restore,
  search: Search,
  send: Send,
  settings: Settings,
  shoppingBasket: ShoppingBasket,
  shoppingCart: ShoppingCart,
  stop: Stop,
};

SVG.AccountBalance = svgMapping.accountBalance;
SVG.Add = svgMapping.add;
SVG.ArrowForwardIos = svgMapping.arrowForwardIos;
SVG.AttachMoney = svgMapping.attachMoney;
SVG.Business = svgMapping.business;
SVG.Cancel = svgMapping.cancel;
SVG.CardGiftcard = svgMapping.cardGiftcard;
SVG.Check = svgMapping.check;
SVG.CheckCircle = svgMapping.checkCircle;
SVG.DepartureBoard = svgMapping.departureBoard;
SVG.Close = svgMapping.close;
SVG.CloudDownload = svgMapping.cloudDownload;
SVG.CorporateFare = svgMapping.corporateFare;
SVG.CloudOff = svgMapping.cloudOff;
SVG.CreditCard = svgMapping.creditCard;
SVG.Delete = svgMapping.delete;
SVG.DirectionCar = svgMapping.directionCar;
SVG.Diversity3 = svgMapping.diversity3;
SVG.East = svgMapping.east;
SVG.Edit = svgMapping.edit;
SVG.Email = svgMapping.email;
SVG.ErrorOutline = svgMapping.errorOutline;
SVG.EventNote = svgMapping.eventNote;
SVG.FileDownload = svgMapping.fileDownload;
SVG.FileDownloadOff = svgMapping.fileDownloadOff;
SVG.FileUpload = svgMapping.fileUpload;
SVG.Group = svgMapping.group;
SVG.HideSource = svgMapping.hideSource;
SVG.HourglassEmpty = svgMapping.hourglassEmpty;
SVG.KeyboardArrowDown = svgMapping.keyboardArrowDown;
SVG.KeyboardArrowUp = svgMapping.keyboardArrowUp;
SVG.Launch = svgMapping.launch;
SVG.Lock = svgMapping.lock;
SVG.LockOpen = svgMapping.lockOpen;
SVG.Logout = svgMapping.logout;
SVG.Logo = svgMapping.logo;
SVG.Menu = svgMapping.menu;
SVG.MoneyOff = svgMapping.moneyOff;
SVG.Notifications = svgMapping.notifications;
SVG.PlayArrow = svgMapping.playArrow;
SVG.QueryStats = svgMapping.queryStats;
SVG.QuestionMark = svgMapping.questionMark;
SVG.Receipt = svgMapping.receipt;
SVG.ReceiptLong = svgMapping.receiptLong;
SVG.Restore = svgMapping.restore;
SVG.Search = svgMapping.search;
SVG.Send = svgMapping.send;
SVG.Settings = svgMapping.settings;
SVG.ShoppingBasket = svgMapping.shoppingBasket;
SVG.ShoppingCart = svgMapping.shoppingCart;
SVG.Stop = svgMapping.stop;

type SVGName = keyof typeof svgMapping;
const SVGNames = Object.keys(svgMapping) as SVGName[];
interface GetSVGReturn {
  ({ color, size }: SVGProps): JSX.Element;
}

function getSVG(svgName: SVGName): GetSVGReturn {
  return svgMapping[svgName];
}

export { getSVG, SVG, type SVGName, SVGNames, type SVGProps };
